import { useCallback, useMemo } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import {
  IStationDto,
  selectedTableRowsIdsAtomFamily,
  StationsBulkActionSourceEnum,
  stationsBulkActionTargetAtom,
  stationsMapBulkPanelOpenAtom,
  stationsMapSelectedIdsAtom,
  tableRowsAtomFamily,
} from "@ryde/shared";

/** State + handlers for the bulk-target panel: the two selections, the source radio, options. */
export const useStationsBulkTarget = () => {
  const [gridSelectedIds, setGridSelectedIds] = useRecoilState(
    selectedTableRowsIdsAtomFamily("station"),
  );
  const [mapSelectedIds, setMapSelectedIds] = useRecoilState(
    stationsMapSelectedIdsAtom,
  );
  const tableRows = useRecoilValue(tableRowsAtomFamily("station"));
  const setIsPanelOpen = useSetRecoilState(stationsMapBulkPanelOpenAtom);

  // Radio choice lives in the atom (the single source of truth the bulk selector reads),
  // so it persists across panel open/close and stays in sync with what bulk actions target.
  const [bulkTarget, setBulkTarget] = useRecoilState(
    stationsBulkActionTargetAtom,
  );
  const source = bulkTarget.source;

  const setSource = useEvent((nextSource: StationsBulkActionSourceEnum) =>
    setBulkTarget(prev => ({ ...prev, source: nextSource })),
  );

  const closePanel = useEvent(() => {
    // Reset the target back to the checkbox selection whenever the panel closes.
    setBulkTarget(prev => ({
      ...prev,
      source: StationsBulkActionSourceEnum.checkbox,
    }));
    setIsPanelOpen(false);
  });

  const allStations = useMemo(
    () => (tableRows as unknown as IStationDto[]) ?? [],
    [tableRows],
  );

  const labelById = useMemo(
    () =>
      new Map(
        allStations.map(station => [
          station.id,
          station.name || `#${station.id}`,
        ]),
      ),
    [allStations],
  );

  const getOptionLabel = useCallback(
    (id: number) => labelById.get(id) ?? `#${id}`,
    [labelById],
  );

  // Dropdown 1 options: every station. Dropdown 2 options: only the checkbox-selected ones.
  const allStationIds = useMemo(
    () => allStations.map(station => station.id),
    [allStations],
  );

  const isAllCheckboxSelected =
    allStationIds.length > 0 && gridSelectedIds.length === allStationIds.length;

  const toggleAllCheckbox = useEvent(() =>
    setGridSelectedIds(isAllCheckboxSelected ? [] : allStationIds),
  );

  const isAllMarkersSelected =
    gridSelectedIds.length > 0 &&
    mapSelectedIds.length === gridSelectedIds.length;

  const toggleAllMarkers = useEvent(() =>
    setMapSelectedIds(isAllMarkersSelected ? [] : gridSelectedIds),
  );

  return {
    source,
    setSource,
    closePanel,
    gridSelectedIds,
    setGridSelectedIds,
    mapSelectedIds,
    setMapSelectedIds,
    allStationIds,
    getOptionLabel,
    isAllCheckboxSelected,
    toggleAllCheckbox,
    isAllMarkersSelected,
    toggleAllMarkers,
  };
};
